/**
 * store/accounting.ts
 * Zustand store for multi-store accounting with localStorage persistence.
 * - Each store has isolated data slices: sales, expenses, balance, gateways, closures.
 * - Backward compatible migration from single-store schema.
 * - Top-level mirrors always reflect the active store for existing components.
 */

import { create } from 'zustand'
import { addDays, isAfter, isBefore, startOfDay } from 'date-fns'
import type {
  Transaction,
  Summary,
  Gateway,
  Expense,
  PaymentMethod,
  MonthlyClosure,
  StoreMeta,
  StoreData,
} from '../types/accounting'
import { toISOAtStartOfDay } from '../lib/format'

/**
 * AccountingState
 * Root state with multi-store support and active store mirror.
 */
interface AccountingState {
  // Multi-store containers
  stores: Record<string, StoreData>
  storesMeta: Record<string, StoreMeta>
  activeStoreId: string

  // Mirrors for active store (to keep existing components working)
  transactions: Transaction[]
  expenses: Expense[]
  currentAccountBalance: number
  gateways: string[]
  closures: Record<string, MonthlyClosure>

  // Store management
  addStore: (input: { name: string; logoUrl?: string }) => string
  updateStore: (id: string, patch: Partial<{ name: string; logoUrl?: string }>) => void
  removeStore: (id: string) => void
  setActiveStore: (id: string) => void

  // Sales actions (active store)
  addTransaction: (input: {
    description: string
    gateway: Gateway
    amountGross: number
    depositDate: string // ISO or HTML date input (YYYY-MM-DD)
  }) => void
  updateTransaction: (
    id: string,
    patch: Partial<{
      description: string
      gateway: Gateway
      amountGross: number
      depositDate: string // ISO or HTML date input
    }>
  ) => void
  removeTransaction: (id: string) => void
  processDueDeposits: (now?: Date) => Transaction[]

  // Expense actions (active store)
  addExpense: (input: {
    description: string
    category: string
    amount: number
    paymentDate: string
    paymentMethod?: PaymentMethod
  }) => void
  updateExpense: (
    id: string,
    patch: Partial<{
      description: string
      category: string
      amount: number
      paymentDate: string
      paymentMethod?: PaymentMethod
    }>
  ) => void
  removeExpense: (id: string) => void
  processDueExpenses: (now?: Date) => Expense[]

  // Gateways actions (active store)
  addGateway: (name: string) => void
  removeGateway: (name: string) => void

  // Summary (active store)
  computeSummary: () => Summary

  // Monthly helpers (active store)
  computeMonthlySummary: (month: string) => MonthlyClosure['totals']
  closeMonth: (month: string, note?: string) => MonthlyClosure
  reopenMonth: (month: string) => void
  getClosure: (month: string) => MonthlyClosure | undefined
}

const DEFAULT_GATEWAYS = ['Mada', 'Apple Pay', 'Visa', 'Mastercard', 'STC Pay', 'PayPal', 'Stripe'] as const

// New schema key
const STORAGE_KEY_V3 = 'accounting-store-v3'
// Backward compatibility with v2 single-store key
const STORAGE_KEY_V2 = 'accounting-store-v2'

/**
 * computeSummaryInternal
 * Derives aggregated metrics from sales only (legacy API).
 */
function computeSummaryInternal(transactions: Transaction[], currentAccountBalance: number): Summary {
  const totalGross = transactions.reduce((s, t) => s + t.amountGross, 0)
  const totalNet = transactions.reduce((s, t) => s + t.netAmount, 0)
  const netPending = transactions.filter((t) => !t.deposited).reduce((s, t) => s + t.netAmount, 0)
  const netDeposited = totalNet - netPending

  const today = startOfDay(new Date())
  const seven = startOfDay(addDays(today, 7))
  const upcoming7Days = transactions
    .filter(
      (t) =>
        !t.deposited && !isBefore(new Date(t.depositDate), today) && !isAfter(new Date(t.depositDate), seven)
    )
    .reduce((s, t) => s + t.netAmount, 0)

  return {
    totalGross,
    totalFees: 0, // fees removed
    totalNet,
    netPending,
    netDeposited,
    currentBalance: currentAccountBalance,
    upcoming7Days,
  }
}

/**
 * inRange
 * Returns true if d is between start and end (inclusive).
 */
function inRange(d: Date, start: Date, end: Date) {
  return !isBefore(d, start) && !isAfter(d, end)
}

/**
 * migrateV2ToV3
 * Converts a single-store persisted payload into multi-store containers.
 */
function migrateV2ToV3(v2: any): {
  stores: Record<string, StoreData>
  storesMeta: Record<string, StoreMeta>
  activeStoreId: string
} {
  const id = 'default'
  const storeData: StoreData = {
    transactions: Array.isArray(v2.transactions) ? (v2.transactions as Transaction[]) : [],
    expenses: Array.isArray(v2.expenses) ? (v2.expenses as Expense[]) : [],
    currentAccountBalance: Number(v2.currentAccountBalance || 0),
    gateways:
      Array.isArray(v2.gateways) && v2.gateways.length
        ? v2.gateways.map((x: unknown) => String(x))
        : [...DEFAULT_GATEWAYS],
    closures: v2.closures && typeof v2.closures === 'object' ? (v2.closures as Record<string, MonthlyClosure>) : {},
  }
  const stores: Record<string, StoreData> = { [id]: storeData }
  const storesMeta: Record<string, StoreMeta> = {
    [id]: { id, name: 'المتجر', createdAt: new Date().toISOString() },
  }
  return { stores, storesMeta, activeStoreId: id }
}

/**
 * loadInitial
 * Loads persisted state from localStorage for v3, or migrates v2 to v3.
 */
function loadInitial(): {
  stores: Record<string, StoreData>
  storesMeta: Record<string, StoreMeta>
  activeStoreId: string
} {
  try {
    const rawV3 = localStorage.getItem(STORAGE_KEY_V3)
    if (rawV3) {
      const data = JSON.parse(rawV3)
      // Basic shape guard
      if (data && typeof data === 'object' && data.stores && data.storesMeta && data.activeStoreId) {
        return data
      }
    }
    // Try v2 and migrate
    const rawV2 = localStorage.getItem(STORAGE_KEY_V2)
    if (rawV2) {
      const v2 = JSON.parse(rawV2)
      return migrateV2ToV3(v2)
    }
  } catch {
    // ignore
  }

  // Fresh install: create one store
  const id = 'default'
  return {
    stores: {
      [id]: {
        transactions: [],
        expenses: [],
        currentAccountBalance: 0,
        gateways: [...DEFAULT_GATEWAYS],
        closures: {},
      },
    },
    storesMeta: {
      [id]: { id, name: 'المتجر', createdAt: new Date().toISOString() },
    },
    activeStoreId: id,
  }
}

/**
 * save
 * Persists root v3 shape to localStorage.
 */
function save(state: {
  stores: Record<string, StoreData>
  storesMeta: Record<string, StoreMeta>
  activeStoreId: string
}) {
  try {
    localStorage.setItem(STORAGE_KEY_V3, JSON.stringify(state))
  } catch {
    // ignore storage errors
  }
}

/**
 * getActive
 * Returns a shallow copy of the active store data for safe mutation.
 */
function getActiveData(state: AccountingState): StoreData {
  return state.stores[state.activeStoreId]
}

/**
 * setActiveMirror
 * Updates top-level mirrors from the given store data.
 */
function setActiveMirror(
  set: any,
  state: AccountingState,
  data: StoreData
) {
  set({
    transactions: data.transactions,
    expenses: data.expenses,
    currentAccountBalance: data.currentAccountBalance,
    gateways: data.gateways,
    closures: data.closures,
  })
}

/**
 * useAccountingStore
 * The Zustand store with multi-store support.
 */
export const useAccountingStore = create<AccountingState>((set, get) => {
  const persisted = loadInitial()
  const initialActive = persisted.stores[persisted.activeStoreId]

  return {
    // Containers
    stores: persisted.stores,
    storesMeta: persisted.storesMeta,
    activeStoreId: persisted.activeStoreId,

    // Mirrors of active store
    transactions: initialActive.transactions,
    expenses: initialActive.expenses,
    currentAccountBalance: initialActive.currentAccountBalance,
    gateways: initialActive.gateways,
    closures: initialActive.closures,

    // Store management
    addStore: (input) => {
      const id = crypto.randomUUID()
      const meta: StoreMeta = {
        id,
        name: input.name?.trim() || 'متجر جديد',
        logoUrl: input.logoUrl?.trim() || undefined,
        createdAt: new Date().toISOString(),
      }
      const data: StoreData = {
        transactions: [],
        expenses: [],
        currentAccountBalance: 0,
        gateways: [...DEFAULT_GATEWAYS],
        closures: {},
      }

      const state = get()
      const stores = { ...state.stores, [id]: data }
      const storesMeta = { ...state.storesMeta, [id]: meta }
      const activeStoreId = id

      set({ stores, storesMeta, activeStoreId })
      // Mirror new active
      setActiveMirror(set, get(), data)

      save({ stores, storesMeta, activeStoreId })
      return id
    },

    updateStore: (id, patch) => {
      const state = get()
      if (!state.storesMeta[id]) return
      const cur = state.storesMeta[id]
      const nextMeta: StoreMeta = {
        ...cur,
        name: patch.name !== undefined ? (patch.name?.trim() || cur.name) : cur.name,
        logoUrl: patch.logoUrl !== undefined ? (patch.logoUrl?.trim() || undefined) : cur.logoUrl,
      }
      const storesMeta = { ...state.storesMeta, [id]: nextMeta }
      set({ storesMeta })
      save({ stores: state.stores, storesMeta, activeStoreId: state.activeStoreId })
    },

    removeStore: (id) => {
      const state = get()
      const ids = Object.keys(state.stores)
      if (!state.stores[id] || ids.length <= 1) {
        // Prevent removing the last store
        return
      }
      const { [id]: _omitData, ...restStores } = state.stores
      const { [id]: _omitMeta, ...restMeta } = state.storesMeta

      // Choose a new active store deterministically (first key)
      const nextActiveId = Object.keys(restStores)[0]

      set({ stores: restStores, storesMeta: restMeta, activeStoreId: nextActiveId })
      // Mirror next active
      setActiveMirror(set, get(), restStores[nextActiveId])
      save({ stores: restStores, storesMeta: restMeta, activeStoreId: nextActiveId })
    },

    setActiveStore: (id) => {
      const state = get()
      if (!state.stores[id]) return
      set({ activeStoreId: id })
      setActiveMirror(set, get(), state.stores[id])
      save({ stores: state.stores, storesMeta: state.storesMeta, activeStoreId: id })
    },

    // Sales
    addTransaction: (input) => {
      const state = get()
      const depositISO = toISOAtStartOfDay(input.depositDate)
      const gross = Number(input.amountGross)
      const net = gross // fees removed

      const tx: Transaction = {
        id: crypto.randomUUID(),
        description: input.description?.trim() || '',
        gateway: input.gateway,
        amountGross: gross,
        netAmount: net,
        depositDate: depositISO,
        createdAt: new Date().toISOString(),
        deposited: false,
      }

      const active = getActiveData(state)
      const nextStore: StoreData = {
        ...active,
        transactions: [tx, ...active.transactions],
      }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }

      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    updateTransaction: (id, patch) => {
      const state = get()
      const active = getActiveData(state)
      const idx = active.transactions.findIndex((t) => t.id === id)
      if (idx === -1) return

      const prev = active.transactions[idx]
      const amountGross = patch.amountGross ?? prev.amountGross
      const netAmount = amountGross
      const depositDate = patch.depositDate ? toISOAtStartOfDay(patch.depositDate) : prev.depositDate

      let deposited = prev.deposited
      let depositedAt = prev.depositedAt
      let balance = active.currentAccountBalance
      const now = new Date()

      if (prev.deposited) {
        // If user moves deposit date to the future, undo the deposit.
        if (isAfter(new Date(depositDate), now)) {
          deposited = false
          depositedAt = undefined
          balance -= prev.netAmount
        } else {
          // Still deposited: adjust balance by the net difference.
          const diff = netAmount - prev.netAmount
          balance += diff
        }
      }

      const updated: Transaction = {
        ...prev,
        description: patch.description ?? prev.description,
        gateway: (patch.gateway as typeof prev.gateway) ?? prev.gateway,
        amountGross,
        netAmount,
        depositDate,
        deposited,
        depositedAt,
      }

      const transactions = [...active.transactions]
      transactions[idx] = updated

      const nextStore: StoreData = {
        ...active,
        transactions,
        currentAccountBalance: balance,
      }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }

      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    removeTransaction: (id) => {
      const state = get()
      const active = getActiveData(state)
      const target = active.transactions.find((t) => t.id === id)
      if (!target) return
      const transactions = active.transactions.filter((t) => t.id !== id)
      const balance = target.deposited ? active.currentAccountBalance - target.netAmount : active.currentAccountBalance

      const nextStore: StoreData = {
        ...active,
        transactions,
        currentAccountBalance: balance,
      }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }

      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    processDueDeposits: (nowParam) => {
      const state = get()
      const now = nowParam ?? new Date()
      const active = getActiveData(state)

      const updated: Transaction[] = []
      const depositedNow: Transaction[] = []
      let currentAccountBalance = active.currentAccountBalance

      for (const t of active.transactions) {
        if (!t.deposited && !isAfter(new Date(t.depositDate), now)) {
          const done: Transaction = {
            ...t,
            deposited: true,
            depositedAt: now.toISOString(),
          }
          depositedNow.push(done)
          updated.push(done)
          currentAccountBalance += t.netAmount
        } else {
          updated.push(t)
        }
      }

      if (depositedNow.length > 0) {
        const nextStore: StoreData = {
          ...active,
          transactions: updated,
          currentAccountBalance,
        }
        const stores = { ...state.stores, [state.activeStoreId]: nextStore }
        set({ stores })
        setActiveMirror(set, get(), nextStore)
        save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
      }

      return depositedNow
    },

    // Expenses
    addExpense: (input) => {
      const state = get()
      const paymentISO = toISOAtStartOfDay(input.paymentDate)
      const amount = Number(input.amount)

      const ex: Expense = {
        id: crypto.randomUUID(),
        description: input.description?.trim() || '',
        category: input.category?.trim() || 'Other',
        amount,
        paymentDate: paymentISO,
        createdAt: new Date().toISOString(),
        paid: false,
        paymentMethod: input.paymentMethod?.trim() || undefined,
      }

      const active = getActiveData(state)
      const nextStore: StoreData = {
        ...active,
        expenses: [ex, ...active.expenses],
      }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }
      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    updateExpense: (id, patch) => {
      const state = get()
      const active = getActiveData(state)
      const idx = active.expenses.findIndex((e) => e.id === id)
      if (idx === -1) return

      const prev = active.expenses[idx]
      const amount = patch.amount ?? prev.amount
      const paymentDate = patch.paymentDate ? toISOAtStartOfDay(patch.paymentDate) : prev.paymentDate

      let paid = prev.paid
      let paidAt = prev.paidAt
      let balance = active.currentAccountBalance
      const now = new Date()

      if (prev.paid) {
        // If user postpones to the future, undo payment and refund to balance.
        if (isAfter(new Date(paymentDate), now)) {
          paid = false
          paidAt = undefined
          balance += prev.amount
        } else {
          // Still paid: adjust balance by the amount difference (more expense => more deduction).
          const diff = amount - prev.amount
          balance -= diff
        }
      }

      const updated: Expense = {
        ...prev,
        description: patch.description ?? prev.description,
        category: patch.category ?? prev.category,
        amount,
        paymentDate,
        paid,
        paidAt,
        paymentMethod: patch.paymentMethod !== undefined ? (patch.paymentMethod || undefined) : prev.paymentMethod,
      }

      const expenses = [...active.expenses]
      expenses[idx] = updated

      const nextStore: StoreData = {
        ...active,
        expenses,
        currentAccountBalance: balance,
      }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }

      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    removeExpense: (id) => {
      const state = get()
      const active = getActiveData(state)
      const target = active.expenses.find((e) => e.id === id)
      if (!target) return
      const expenses = active.expenses.filter((e) => e.id !== id)
      const balance = target.paid ? active.currentAccountBalance + target.amount : active.currentAccountBalance

      const nextStore: StoreData = {
        ...active,
        expenses,
        currentAccountBalance: balance,
      }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }

      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    processDueExpenses: (nowParam) => {
      const state = get()
      const now = nowParam ?? new Date()
      const active = getActiveData(state)

      const updated: Expense[] = []
      const paidNow: Expense[] = []
      let currentAccountBalance = active.currentAccountBalance

      for (const e of active.expenses) {
        if (!e.paid && !isAfter(new Date(e.paymentDate), now)) {
          const done: Expense = {
            ...e,
            paid: true,
            paidAt: now.toISOString(),
          }
          paidNow.push(done)
          updated.push(done)
          currentAccountBalance -= e.amount
        } else {
          updated.push(e)
        }
      }

      if (paidNow.length > 0) {
        const nextStore: StoreData = {
          ...active,
          expenses: updated,
          currentAccountBalance,
        }
        const stores = { ...state.stores, [state.activeStoreId]: nextStore }
        set({ stores })
        setActiveMirror(set, get(), nextStore)
        save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
      }

      return paidNow
    },

    // Gateways
    addGateway: (name) => {
      const state = get()
      const active = getActiveData(state)
      const clean = name.trim()
      if (!clean) return
      if (active.gateways.some((x) => x.toLowerCase() === clean.toLowerCase())) return
      const gateways = [clean, ...active.gateways]
      const nextStore: StoreData = { ...active, gateways }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }
      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },
    removeGateway: (name) => {
      const state = get()
      const active = getActiveData(state)
      const gateways = active.gateways.filter((g) => g !== name)
      const nextStore: StoreData = { ...active, gateways }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }
      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    // Legacy summary for active store
    computeSummary: () => {
      const state = get()
      const active = getActiveData(state)
      return computeSummaryInternal(active.transactions, active.currentAccountBalance)
    },

    // Monthly helpers for active store
    computeMonthlySummary: (month) => {
      const state = get()
      const active = getActiveData(state)

      const [yStr, mStr] = month.split('-')
      const start = new Date(Number(yStr), Number(mStr) - 1, 1, 0, 0, 0, 0)
      const end = new Date(Number(yStr), Number(mStr), 0, 23, 59, 59, 999)

      const txs = active.transactions
      const exs = active.expenses

      // Deposited by event date within month
      const salesDeposited = txs
        .filter((t) => t.deposited && t.depositedAt && inRange(new Date(t.depositedAt), start, end))
        .reduce((s, t) => s + t.netAmount, 0)
      const countSales = txs.filter((t) => t.deposited && t.depositedAt && inRange(new Date(t.depositedAt), start, end))
        .length

      // Pending deposits scheduled within month
      const pendingSales = txs
        .filter((t) => !t.deposited && inRange(new Date(t.depositDate), start, end))
        .reduce((s, t) => s + t.netAmount, 0)

      const expensesPaid = exs
        .filter((e) => e.paid && e.paidAt && inRange(new Date(e.paidAt), start, end))
        .reduce((s, e) => s + e.amount, 0)
      const countExpenses = exs.filter((e) => e.paid && e.paidAt && inRange(new Date(e.paidAt), start, end)).length

      const pendingExpenses = exs
        .filter((e) => !e.paid && inRange(new Date(e.paymentDate), start, end))
        .reduce((s, e) => s + e.amount, 0)

      const netProfit = salesDeposited - expensesPaid

      return {
        salesDeposited,
        expensesPaid,
        netProfit,
        countSales,
        countExpenses,
        pendingSales,
        pendingExpenses,
      }
    },

    closeMonth: (month, note) => {
      const state = get()
      const active = getActiveData(state)
      const totals = get().computeMonthlySummary(month)
      const closure: MonthlyClosure = {
        month,
        closedAt: new Date().toISOString(),
        note,
        totals,
      }
      const closures = { ...active.closures, [month]: closure }
      const nextStore: StoreData = { ...active, closures }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }
      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
      return closure
    },

    reopenMonth: (month) => {
      const state = get()
      const active = getActiveData(state)
      const { [month]: _omit, ...rest } = active.closures
      const nextStore: StoreData = { ...active, closures: rest }
      const stores = { ...state.stores, [state.activeStoreId]: nextStore }
      set({ stores })
      setActiveMirror(set, get(), nextStore)
      save({ stores, storesMeta: state.storesMeta, activeStoreId: state.activeStoreId })
    },

    getClosure: (month) => {
      const state = get()
      return getActiveData(state).closures[month]
    },
  }
})
